<template lang="pug">
div(:class="{ 'has-data': packages.length > 0 }")
  //- Mobile card list view
  template(v-if="isMobile")
    //- Loading skeleton
    template(v-if="loading")
      v-card.rounded-lg.mb-3(v-for="n in 3" :key="'skeleton-' + n" flat outlined)
        .pa-4
          v-skeleton-loader(type="list-item-three-line")

    //- Empty state
    .text-center.py-8(v-else-if="!packages.length")
      .yz-regular-body-sm.neutral500--text No items found

    //- Card list
    template(v-else)
      v-card.rounded-lg.mb-3(v-for="(item, index) in packages" :key="item.id" flat outlined @click="onEditItem(item)")
        .pa-4
          //- Header: Name + HQ badge + 3-dot menu
          .d-flex.align-center.justify-space-between
            .d-flex.align-center.justify-space-between.flex-grow-1(style="min-width: 0;")
              .yz-medium-body-sm.neutral700--text.text-truncate {{ item.product.name }}
              yz-badge(x-small color="blue-100" text-color="blue-500" border) HQ
            v-menu(offset-y left rounded="lg")
              template(#activator="{ on }")
                yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="sm" v-on="on" @click.native.stop)
              yz-list.py-0
                yz-list-item(@click="onEditItem(item)")
                  div Edit package
                yz-divider
                yz-list-item(@click="onPackageHistory(item)")
                  div Package history
                yz-divider
                yz-list-item(@click="onChangeStatus(item)")
                  div(:class="item.product.status === 'published' ? 'danger500--text' : 'success500--text'") {{ item.product.status === 'published' ? 'Archive' : 'Activate' }}
                yz-divider
                yz-list-item(@click="onDeleteItem(item)")
                  div.danger500--text Delete package

          //- Stock
          .d-flex.align-center.mt-3(v-if="item.available_qty !== null && item.available_qty !== undefined")
            .yz-regular-body-xs.neutral500--text.mr-2 Stock
            .yz-regular-body-xs.text-neutral-500.font-italic(v-if="item.available_qty === false") Not applicable
            .yz-regular-body-xs.neutral700--text(v-else-if="item.available_qty > 0") {{ item.available_qty }} in stock
            .yz-regular-body-xs.danger500--text(v-else-if="item.available_qty === 0") 0 in stock
            .yz-regular-body-xs.neutral700--text(v-else) -

          //- Price
          .d-flex.align-center.mt-2(v-if="item.product && item.product.price")
            .yz-regular-body-xs.neutral500--text.mr-2 Price
            .yz-regular-body-xs.success500--text {{ item.product.price | displayPrice($currency) }}

          //- Branches
          .d-flex.align-center.mt-2(v-if="item.assigned_branches && item.assigned_branches.length")
            .yz-regular-body-xs.neutral500--text.mr-2 Branches
            yz-truncated-list(:items="item.assigned_branches" text-item="name" :gap="4")

  //- Desktop table view
  .table-scroll-wrapper(v-else)
    yz-data-table(:headers="tableHeaders" :items="packages" item-key="id" :loading="loading" :server-items-length="totalCount" no-outer-border radius="0" disable-sort body-height="52px" header-height="36px" header-class="yz-bold-body-xs" hide-default-footer :items-per-page="pageSize" @click:row="onEditItem")
      //- Name
      template(v-slot:item.name="{ item }")
        .d-flex.align-center.justify-space-between
          .yz-medium-body-sm.neutral700--text.mr-2 {{ item.product.name }}
          yz-badge(x-small color="blue-100" text-color="blue-500" border) HQ
      //- Branches
      template(v-slot:item.branches="{ item }")
        yz-truncated-list(v-if="item.assigned_branches && item.assigned_branches.length" :items="item.assigned_branches" text-item="name" :gap="4")
      //- Stock
      template(v-slot:item.stock="{ item }")
        .yz-regular-body-sm.text-neutral-500.font-italic(v-if="item.available_qty === false") Not applicable
        .yz-regular-body-sm.neutral700--text(v-else-if="item.available_qty === null") -
        .yz-regular-body-sm.neutral700--text(v-else-if="item.available_qty > 0") {{ item.available_qty }} in stock
        .yz-regular-body-sm.danger500--text(v-else-if="item.available_qty === 0") 0 in stock
        .yz-regular-body-sm.neutral700--text(v-else) -
      //- Price
      template(v-slot:item.price="{ item }")
        .yz-regular-body-sm.success500--text(v-if="item.product && item.product.price") {{ item.product.price | displayPrice($currency) }}
      //- Items
      template(v-slot:item.items="{ item }")
        div.py-3(v-if="item.treatments && item.treatments.length")
          template(v-for="treatment in item.treatments")
            v-row(dense)
              v-col(cols="5")
                .yz-regular-body-sm.neutral700--text {{ treatment.name }}
              v-col(cols="2")
                .yz-regular-body-sm.neutral700--text QTY: {{ treatment.quantity_per_set }} {{ getTreatmentUnit(treatment) }}
              v-col(cols="3")
                .yz-regular-body-sm.text-neutral-500.font-italic(v-if="treatment.deleted") Not applicable
                .yz-regular-body-sm.neutral700--text(v-else-if="treatment.inventory_enabled") {{ treatment.inventory_qty }} in stock
                .yz-regular-body-sm.neutral700--text(v-else) -
              v-col(cols="2")
                .yz-regular-body-sm.neutral700--text {{ treatment.price | displayPrice($currency) }}
      //- Status
      template(v-slot:item.status="{ item }")
        yz-badge(size="md" :color="item.product.status === 'published' ? 'green-100' : 'red-100'" :text-color="item.product.status === 'published' ? 'success-500' : 'danger-500'")
          .status-dot.mr-1(:class="item.product.status === 'published' ? 'active' : 'archived'")
          span {{ item.product.status === 'published' ? 'Active' : 'Archived' }}
      //- Actions
      template(v-slot:item.actions="{ item, index }")
        .d-flex.align-center.justify-end(@click.stop)
          v-menu(offset-y :top="menuPositionTop(index)" left rounded="lg")
            template(#activator="{ on }")
              yz-btn(icon-only icon="$yzi-dots-horizontal" icon-size="20" size="lg" v-on="on")
            yz-list.py-0
              yz-list-item(@click="onEditItem(item)")
                div Edit package
              yz-divider
              yz-list-item(@click="onPackageHistory(item)")
                div Package history
              yz-divider
              yz-list-item(@click="onChangeStatus(item)")
                div(:class="item.product.status === 'published' ? 'neutral700--text' : 'success500--text'") {{ item.product.status === 'published' ? 'Archive' : 'Activate' }}
              yz-divider
              yz-list-item(@click="onDeleteItem(item)")
                div.danger500--text Delete package

  //- Pagination
  .d-flex.align-center.mt-4(v-if="totalCount > pageSize")
    v-spacer
    yz-pagination(v-model="currentPage" version="yc" :page-size.sync="pageSize" :count="totalCount" @input="onPageChanged")
  package-uom-warning-dialog(v-if="showUomWarningDialog" :open.sync="showUomWarningDialog" :treatments="uomWarningTreatments")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PackageUomWarningDialog from '@/components/clinic/inventory/PackageUomWarningDialog.vue'

export default {
  components: { PackageUomWarningDialog },
  props: {
    isMobile: { type: Boolean, default: false },
    activeTab: { type: Number, default: 0 },
    searchText: { type: String, default: '' },
    filters: { type: Array, default: () => [] },
    locationFilter: { default: null }
  },
  data: () => ({
    loading: false,
    packages: [],
    totalCount: 0,
    currentPage: 1,
    pageSize: 20,
    showUomWarningDialog: false,
    uomWarningTreatments: []
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hqPackagesFields: 'hq/getPackagesFields'
    }),
    tableHeaders () {
      const name = { text: 'NAME', value: 'name', sortable: false }
      const fields = this.hqPackagesFields.filter(item => item.enable).map(item => ({
        text: item.field_name.toUpperCase(),
        value: item.id,
        sortable: false,
        ...(item.width ? { width: item.width } : {})
      }))
      const actions = { text: '', value: 'actions', sortable: false, width: 80 }
      return [name, ...fields, actions]
    }
  },
  watch: {
    activeTab () {
      this.currentPage = 1
      this.fetchPackages()
    },
    filters () {
      this.currentPage = 1
      this.fetchPackages()
    },
    locationFilter () {
      this.currentPage = 1
      this.fetchPackages()
    }
  },
  mounted () {
    this.fetchPackages()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPackages () {
      this.loading = true
      const params = {
        page: this.currentPage,
        page_size: this.pageSize
      }
      // Tab filters
      if (this.activeTab === 1) params.status = 'published'
      if (this.activeTab === 2) params.no_stock = true
      if (this.activeTab === 3) params.status = 'draft'
      // Search
      if (this.searchText) params.search = this.searchText
      // Filters
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      // Location filter
      if (this.locationFilter) params.branches = this.locationFilter.join(',')

      this.$axios.get(`/clinics/${this.company.slug}/hq/packages`, { params })
        .then(resp => {
          this.packages = resp.data.results || []
          this.totalCount = resp.data.count || 0
        })
        .catch(err => {
          const message = err.response?.data?.detail
          if (message && message.includes('Invalid page')) {
            this.currentPage = 1
            this.fetchPackages()
          } else {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    onPageChanged () {
      this.fetchPackages()
    },
    onEditItem (item) {
      this.$emit('edit-item', item)
    },
    onPackageHistory (item) {
      this.$router.push({ name: 'HQPackageHistory', params: { id: item.id } })
    },
    onChangeStatus (item) {
      const newStatus = item.product.status === 'published' ? 'draft' : 'published'
      if (newStatus === 'published') {
        const invalidTreatments = this.getInvalidUomTreatments(item.treatments || [])
        if (invalidTreatments.length) {
          this.uomWarningTreatments = invalidTreatments
          this.showUomWarningDialog = true
          return
        }
      }
      this.$axios.patch(`/clinics/${this.company.slug}/hq/packages/${item.id}`, { product: { status: newStatus } })
        .then(() => {
          const label = newStatus === 'draft' ? 'archived' : 'activated'
          this.showMessage(['success', `Package ${label} successfully`])
          this.fetchPackages()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    onDeleteItem (item) {
      this.$emit('delete-item', item)
    },
    getTreatmentUnit (treatment) {
      if (treatment.uom_conversion_enabled && treatment.uom_conversions && treatment.uom_conversions.length) {
        return treatment.uom_conversions[treatment.uom_conversions.length - 1].convert_to || ''
      }
      return (treatment.per_set && treatment.per_set.uom) || ''
    },
    getInvalidUomTreatments (treatments) {
      const invalid = []
      treatments.forEach(t => {
        if (!t.uom_conversion_enabled || !t.uom_conversions || !t.uom_conversions.length) return
        const validUnits = []
        t.uom_conversions.forEach(c => {
          if (c.uom && !validUnits.includes(c.uom)) validUnits.push(c.uom)
          if (c.convert_to && !validUnits.includes(c.convert_to)) validUnits.push(c.convert_to)
        })
        const currentUom = (t.per_set && t.per_set.uom) || t.uom || null
        if (!currentUom || !validUnits.includes(currentUom)) {
          invalid.push({ name: t.name, currentUom: currentUom || '—', availableUoms: validUnits.join(', ') })
        }
      })
      return invalid
    },
    menuPositionTop (index) {
      const position = index + 1
      if (position > 7) {
        const start = this.packages.length - 3
        if (position > start) return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.has-data ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
.table-scroll-wrapper {
  overflow-x: auto;
}
.table-scroll-wrapper ::v-deep .yz-data-table {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper > table {
  min-width: 1400px;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  &.active { background-color: var(--yz-success-500); }
  &.archived { background-color: var(--yz-danger-500); }
}
</style>
